import type { Question, VisitorAnswer } from './types';

/**
 * Small helpers for turning answers into words.
 *
 * Separate from `scoring.ts` because none of this affects the result, and
 * separate from `contact.ts` because the journey needs it too — the creator's
 * reply after each question is rendered with exactly the same code that
 * renders a visitor's answer in the outbound payload, so the two can never
 * drift apart.
 */

/** Reframes the creator's stored answer as an ordinary answer to the same question. */
export function creatorAnswerAsAnswer(question: Question): VisitorAnswer {
  switch (question.creatorAnswer.kind) {
    case 'single':
      return {
        questionId: question.id,
        kind: 'single',
        optionId: question.creatorAnswer.optionId,
      };
    case 'multi':
      return {
        questionId: question.id,
        kind: 'multi',
        optionIds: question.creatorAnswer.optionIds,
      };
    case 'scale':
      return { questionId: question.id, kind: 'scale', value: question.creatorAnswer.value };
  }
}

/** Renders one answer as human-readable text. */
export function describeAnswer(question: Question, answer: VisitorAnswer): string {
  switch (answer.kind) {
    case 'single': {
      const option = question.options?.find((o) => o.id === answer.optionId);
      return option?.label ?? answer.optionId;
    }
    case 'multi': {
      const labels = answer.optionIds.map(
        (id) => question.options?.find((o) => o.id === id)?.label ?? id,
      );
      return labels.length > 0 ? labels.join(', ') : '—';
    }
    case 'scale': {
      const described = question.scale?.valueLabels?.[answer.value];
      const max = question.scale?.max ?? 5;
      return described ? `${described} (${answer.value} of ${max})` : `${answer.value} of ${max}`;
    }
    default:
      return '—';
  }
}
